import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  MenuItem,
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Modal,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  Box,
  Checkbox
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import GoogleMapsAutoComplete from '../components/GoogleMapsAutoComplete';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import PaymentGatewayModal from "../components/PaymentGatewayModal";
import { makeStyles } from '@mui/styles';
import UsersCombo from '../components/UsersCombo';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { BookingModalBody, validateBookingObj } from 'common/sharedFunctions';
import { useLocation, useNavigate } from 'react-router-dom';
import { calcEst, showEst, optionsRequired } from '../common/sharedFunctions';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import moment from "moment";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

// Icons
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleIcon from '@mui/icons-material/People';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AppsIcon from '@mui/icons-material/Apps';

const useStyles = makeStyles(theme => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  modal: {
    display: 'flex',
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '8px',
      alignItems: 'flex-end',
    },
  },
  paper: {
    width: 480,
    maxWidth: "95%",
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "var(--bg-card)",
    border: "var(--card-border)",
    borderRadius: "16px",
    boxShadow: "var(--shadow)",
    padding: "28px",
    outline: "none",
    color: "var(--text-main)",
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      maxHeight: '85vh',
      borderRadius: '20px 20px 0 0',
      padding: '20px 16px',
      margin: 0,
    },
    '&::-webkit-scrollbar': {
      width: '10px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(233, 174, 49, 0.3)',
      borderRadius: '10px',
      borderLeft: '3px solid transparent',
      borderRight: '3px solid transparent',
      borderTop: '10px solid transparent',
      borderBottom: '10px solid transparent',
      backgroundClip: 'padding-box',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(233, 174, 49, 0.6)',
    }
  },
  container: {
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: 'var(--bg-card)',
    borderRadius: "24px",
    width: '100%',
    maxWidth: '1200px',
    boxShadow: 'var(--shadow)',
    border: 'var(--card-border)',
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box'
  },
  title: {
    color: 'var(--text-light)',
    padding: '20px 32px',
    background: 'var(--table-header-bg)',
    fontSize: 22,
    fontWeight: 700,
    fontFamily: 'Outfit, sans-serif',
  },
  gridcontainer: {
    alignContent: 'center'
  },
  items: {
    margin: 0,
    width: '100%'
  },
  input: {
    fontSize: 16,
    color: "var(--text-main)",
    '& .MuiInputBase-input': {
      color: 'var(--text-main)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'var(--table-input-bg)',
      color: 'var(--text-main)',
      '& fieldset': {
        borderColor: 'var(--border-color)',
      },
      '&:hover fieldset': {
        borderColor: 'var(--primary)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--primary)',
      },
    },
  },
  inputdimmed: {
    fontSize: 16,
    color: "var(--text-secondary)",
    '& .MuiInputBase-input': {
      color: 'var(--text-secondary)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'var(--table-input-bg)',
      color: 'var(--text-secondary)',
      '& fieldset': {
        borderColor: 'var(--border-color)',
      },
      '&:hover fieldset': {
        borderColor: 'var(--primary)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--primary)',
      },
    },
  },
  buttonStyle1: {
    background: 'linear-gradient(135deg, #E9AE31 0%, #CFA02D 100%)',
    color: '#fff',
    borderRadius: "10px",
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(233, 174, 49, 0.25)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(233, 174, 49, 0.4)',
      transform: 'translateY(-1px)',
    }
  },
  buttonStyle2: {
    border: '1px solid var(--border-color)',
    color: 'var(--text-main)',
    background: 'transparent',
    borderRadius: "10px",
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'var(--table-row-selected)',
      borderColor: 'var(--primary)',
    }
  },
  inputRtl: {
    "& label": {
      right: 25,
      left: "auto"
    },
    "& legend": {
      textAlign: "right",
      marginRight: 18
    },
    "& label.Mui-focused": {
      color: 'var(--primary)',
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: 'var(--primary)',
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: 'var(--primary)',
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: 'var(--table-input-bg)',
      color: 'var(--text-main)',
      "&.Mui-focused fieldset": {
        borderColor: 'var(--primary)',
      },
    },
  },
  rightRty: {
    "& legend": {
      marginRight: 30
    }
  },
  textField: {
    "& label.Mui-focused": {
      color: 'var(--primary)',
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: 'var(--primary)',
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: 'var(--primary)',
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: 'var(--table-input-bg)',
      color: 'var(--text-main)',
      "&.Mui-focused fieldset": {
        borderColor: 'var(--primary)',
      },
    },
  },
}));

const icons = {
  'paypal': require('../assets/payment-icons/paypal-logo.png').default,
  'braintree': require('../assets/payment-icons/braintree-logo.png').default,
  'stripe': require('../assets/payment-icons/stripe-logo.png').default,
  'paytm': require('../assets/payment-icons/paytm-logo.png').default,
  'payulatam': require('../assets/payment-icons/payulatam-logo.png').default,
  'flutterwave': require('../assets/payment-icons/flutterwave-logo.png').default,
  'paystack': require('../assets/payment-icons/paystack-logo.png').default,
  'securepay': require('../assets/payment-icons/securepay-logo.png').default,
  'payfast': require('../assets/payment-icons/payfast-logo.png').default,
  'liqpay': require('../assets/payment-icons/liqpay-logo.png').default,
  'culqi': require('../assets/payment-icons/culqi-logo.png').default,
  'mercadopago': require('../assets/payment-icons/mercadopago-logo.png').default,
  'squareup': require('../assets/payment-icons/squareup-logo.png').default,
  'wipay': require('../assets/payment-icons/wipay-logo.png').default,
  'test': require('../assets/payment-icons/test-logo.png').default,
  'razorpay': require('../assets/payment-icons/razorpay-logo.png').default,
  'paymongo': require('../assets/payment-icons/paymongo-logo.png').default,
  'skipcash': require('../assets/payment-icons/skipcash-logo.png').default,
}

const generateTimeSlots = () => {
  const slots = [];
  const start = moment().startOf('day');
  const end = moment().endOf('day');
  while (start.isBefore(end)) {
    slots.push(start.format('hh:mm A'));
    start.add(15, 'minutes');
  }
  return slots;
};

export default function AddBookings(props) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    getEstimate,
    clearEstimate,
    addBooking,
    clearBooking,
    MinutesPassed,
    GetDateString,
    GetDistance,
    updateBooking,
    updateProfileWithEmail,
    checkUserExists,
    editPromo,
    fetchPromos
  } = api;
  const dispatch = useDispatch();
  const classes = useStyles();
  const cartypes = useSelector(state => state.cartypes.cars);
  const estimatedata = useSelector(state => state.estimatedata);
  const bookingdata = useSelector(state => state.bookingdata);
  const userdata = useSelector(state => state.usersdata);
  const auth = useSelector(state => state.auth);
  const promodata = useSelector(state => state.promodata);

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    dispatch(fetchPromos());
  }, [dispatch, fetchPromos]);
  const settings = useSelector(state => state.settingsdata.settings);
  let providers = useSelector(state => state.paymentmethods.providers);
  if (providers && !Array.isArray(providers)) {
    let arr = [];
    let pms = Object.keys(providers);
    for (let i = 0; i < pms.length; i++) {
      if (providers[pms[i]].active) {
        arr.push({
          name: pms[i],
          link: '/' + pms[i] + '-link',
          ...providers[pms[i]]
        });
      }
    }
    providers = arr;
  }
  const activeBookings = useSelector(state => state.bookinglistdata.active);
  const [carType, setCarType] = useState(t('select_car'));
  const [pickupAddress, setPickupAddress] = useState(null);
  const [dropAddress, setDropAddress] = useState(null);
  const [optionModalStatus, setOptionModalStatus] = useState(false);
  const [bookingMode, setBookingMode] = useState('both'); // 'passenger', 'parcel', 'both'
  const [ratingBooking, setRatingBooking] = useState(null);
  const [openRatingDialog, setOpenRatingDialog] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");

  const handleSkipRating = () => {
    if (!ratingBooking) return;
    setOpenRatingDialog(false);
    const updated = {
      ...ratingBooking,
      rating: 0,
      feedback: "-"
    };
    dispatch(updateBooking(updated));
  };

  const handleSubmitRating = () => {
    if (!ratingBooking || ratingValue === 0) return;
    setOpenRatingDialog(false);
    const updated = {
      ...ratingBooking,
      rating: ratingValue,
      feedback: feedbackText.trim() !== "" ? feedbackText : "-"
    };
    dispatch(updateBooking(updated));
  };

  useEffect(() => {
    if (auth.profile && auth.profile.usertype === 'customer' && activeBookings) {
      const pendingRating = activeBookings.find(
        (b) => b.status === 'COMPLETE' && b.rating === undefined
      );
      if (pendingRating) {
        setRatingBooking(pendingRating);
        setOpenRatingDialog(true);
      } else {
        setOpenRatingDialog(false);
        setRatingBooking(null);
      }
    }
  }, [activeBookings, auth.profile]);
  const [estimateModalStatus, setEstimateModalStatus] = useState(false);
  const [selectedCarDetails, setSelectedCarDetails] = useState(null);
  const isBusOrVan = selectedCarDetails && (selectedCarDetails.name.toUpperCase().includes('BUS') || selectedCarDetails.name.toUpperCase().includes('VAN') || selectedCarDetails.name.toUpperCase().includes('PAX'));
  const [users, setUsers] = useState(null);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [userCombo, setUserCombo] = useState(null);
  const [estimateRequested, setEstimateRequested] = useState(false);
  const [bookingType, setBookingType] = useState('Book Now');
  const rootRef = useRef(null);
  const [tempRoute, setTempRoute] = useState();
  const [drivers, setDrivers] = useState([]);
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const [walletModalStatus, setWalletModalStatus] = useState(false);
  const { state } = useLocation();
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [roundTrip, setRoundTrip] = useState(0);
  const [tripInstructions, setTripInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingOnWait, setBookingOnWait] = useState();
  const [deliveryWithBid, setDeliveryWithBid] = useState(0);
  const [otherPerson, setOtherPerson] = useState(false);

  useEffect(() => {
    if (settings && settings.bookingFlow) {
      setDeliveryWithBid(settings.bookingFlow === "2" ? 1 : 0)
    }
  }, [settings, settings.bookingFlow])

  const [instructionData, setInstructionData] = useState({
    otherPerson: "",
    otherPersonPhone: "",
    pickUpInstructions: "",
    deliveryInstructions: "",
    parcelTypeIndex: 0,
    optionIndex: 0,
    parcelTypeSelected: null,
    optionSelected: null,
    parcelActive: true,
    optionActive: true,
    parcelQuantity: 1,
    passengerCount: 1
  });

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const [payment_mode, setPaymentMode] = useState(0);
  const [radioProps, setRadioProps] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (settings && providers) {
      setSelectedProvider(providers[0]);
      let arr = [{ label: t('wallet'), value: 0, cat: 'wallet' }];
      let val = 0;
      if (!settings.disable_online && providers && providers.length > 0) {
        val++;
        arr.push({ label: t('card'), value: val, cat: 'card' });
      }
      if (!settings.disable_cash) {
        val++;
        arr.push({ label: t('cash'), value: val, cat: 'cash' });
      }
      setRadioProps(arr);
    }
  }, [settings, providers, t]);;

  useEffect(() => {
    if (state && state !== null) {
      let carDetails = state.carData;
      setCarType(carDetails.name)
      setInstructionData({
        deliveryPerson: "",
        deliveryPersonPhone: "",
        pickUpInstructions: "",
        deliveryInstructions: "",
        parcelTypeIndex: 0,
        optionIndex: 0,
        parcelTypeSelected: Array.isArray(carDetails.parcelTypes) ? carDetails.parcelTypes[0] : null,
        optionSelected: Array.isArray(carDetails.options) ? carDetails.options[0] : null
      })
      setSelectedCarDetails(carDetails);
    }
  }, [state]);

  const handleChange = (e) => {
    if (e.target.name === 'parcelTypeIndex') {
      setInstructionData({
        ...instructionData,
        parcelTypeIndex: parseInt(e.target.value),
        parcelTypeSelected: selectedCarDetails.parcelTypes[e.target.value]
      });
    } else if (e.target.name === 'optionIndex') {
      setInstructionData({
        ...instructionData,
        optionIndex: parseInt(e.target.value),
        optionSelected: selectedCarDetails.options[e.target.value]
      });
    } else if (e.target.name === 'payment_mode') {
      setPaymentMode(e.target.value);
    } else if (e.target.name === 'selectedProviderIndex') {
      setSelectedProviderIndex(parseInt(e.target.value));
      setSelectedProvider(providers[parseInt(e.target.value)]);
    } else if (e.target.name === 'tripInstructions') {
      setTripInstructions(e.target.value);
    } else if (e.target.name === 'roundTrip') {
      setRoundTrip(e.target.value);
    } else if (e.target.name === 'bookingFlow') {
      setDeliveryWithBid(e.target.value);
    } else if (e.target.name === 'firstName') {
      setProfileData({ ...profileData, [e.target.name]: e.target.value });
    } else if (e.target.name === 'lastName') {
      setProfileData({ ...profileData, [e.target.name]: e.target.value });
    } else if (e.target.name === 'email') {
      setProfileData({ ...profileData, [e.target.name]: e.target.value });
    } else {
      setInstructionData({ ...instructionData, [e.target.name]: e.target.value });
    }
  };

  const roundToNearest15 = (dateTime) => {
    if (!dateTime) return dateTime;
    const m = moment(dateTime);
    const remainder = m.minutes() % 15;
    if (remainder !== 0) {
      m.minutes(Math.round(m.minutes() / 15) * 15);
    }
    return m.format('YYYY-MM-DDTHH:mm');
  };

  const [selectedDate, setSelectedDate] = useState(() => {
    const initial = moment();
    const remainder = initial.minutes() % 15;
    if (remainder !== 0) {
      initial.minutes(Math.round(initial.minutes() / 15) * 15);
    }
    return initial.format('YYYY-MM-DDTHH:mm');
  });

  const clearForm = () => {
    setUserCombo(null);
    setPickupAddress(null);
    setDropAddress(null);
    setSelectedCarDetails(null);
    setCarType(t('select_car'));
    setBookingType('Book Now');
    setEstimateRequested(false);
  }

  useEffect(() => {
    if (auth.profile && bookingOnWait) {
      setEstimateModalStatus(false);
      dispatch(addBooking(bookingOnWait));
      setBookingOnWait(null);
      setLoading(false);
    }
  }, [auth.profile, bookingOnWait, dispatch, addBooking])

  useEffect(() => {
    if (bookingdata.booking && bookingdata.booking.mainData.status === 'PAYMENT_PENDING') {
      if (bookingdata.booking.mainData.payment_mode === 'cash') {
        dispatch(clearBooking());
        setTempRoute(null);
        navigate('/bookings')
      } else {
        if (bookingdata.booking.mainData.payment_mode === 'card') {
          setPaymentModalStatus(true);
        } else {
          setWalletModalStatus(true);
        }
      }
    }
    if (bookingdata.booking && bookingdata.booking.mainData.status === 'NEW') {
      dispatch(clearBooking());
      setTempRoute(null);
      navigate('/bookings')
    }
  }, [bookingdata.booking, clearBooking, dispatch, navigate]);


  const handleCarCardClick = (car) => {
    setCarType(car.name);
    let instObj = { ...instructionData };
    if (Array.isArray(car.parcelTypes)) {
      instObj.parcelTypeSelected = car.parcelTypes[0];
      instObj.parcelTypeIndex = 0;
      instObj.parcelActive = true;
      instObj.parcelQuantity = 1;
    } else {
      instObj.parcelTypeSelected = null;
      instObj.parcelActive = false;
      instObj.parcelQuantity = 0;
    }
    // Check if the vehicle is a bus or mini van
    const isBusOrVan = car && (car.name.toUpperCase().includes('BUS') || car.name.toUpperCase().includes('VAN') || car.name.toUpperCase().includes('PAX'));
    
    if (isBusOrVan) {
      instObj.optionActive = true;
      instObj.passengerCount = 1;
      if (Array.isArray(car.options) && car.options.length > 0) {
        instObj.optionSelected = car.options[0];
        instObj.optionIndex = 0;
      } else {
        instObj.optionSelected = null;
        instObj.optionIndex = 0;
      }
    } else {
      instObj.optionSelected = null;
      instObj.optionActive = false;
      instObj.passengerCount = 0;
    }
    setInstructionData(instObj);
    setSelectedCarDetails(car);
    // Set default booking mode based on available types
    const hasParcel = Array.isArray(car.parcelTypes) && car.parcelTypes.length > 0;
    const hasOptions = isBusOrVan;
    if (hasParcel && hasOptions) {
      setBookingMode('both');
    } else if (hasParcel) {
      setBookingMode('parcel');
    } else if (hasOptions) {
      setBookingMode('passenger');
    } else {
      setBookingMode('both');
    }
  };


  const onDateChange = (value) => {
    if (value && typeof value === 'object' && value.isValid) {
      if (value.isValid()) {
        setSelectedDate(roundToNearest15(value));
      }
    } else if (value && value.target) {
      setSelectedDate(roundToNearest15(value.target.value));
    } else {
      setSelectedDate(roundToNearest15(value));
    }
  };

  const handleDatePickerChange = (dateVal) => {
    if (dateVal && dateVal.isValid()) {
      const newDateTime = moment(selectedDate)
        .year(dateVal.year())
        .month(dateVal.month())
        .date(dateVal.date());
      setSelectedDate(roundToNearest15(newDateTime));
    }
  };

  const handleTimePickerChange = (e) => {
    const timeStr = e.target.value;
    const parsedTime = moment(timeStr, 'HH:mm');
    const newDateTime = moment(selectedDate)
      .hours(parsedTime.hours())
      .minutes(parsedTime.minutes());
    setSelectedDate(roundToNearest15(newDateTime));
  };

  useEffect(() => {
    if (estimatedata.estimate && estimateRequested) {
      setEstimateRequested(false);
      setEstimateModalStatus(true);
    }
    if (userdata.users) {
      let arr = [];
      for (let i = 0; i < userdata.users.length; i++) {
        let user = userdata.users[i];
        if (user.usertype === 'customer' && ((auth.profile.usertype === 'fleetadmin' && user.fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin')) {
          arr.push({
            'firstName': user.firstName,
            'lastName': user.lastName,
            'mobile': user.mobile,
            'email': user.email,
            'uid': user.id,
            'desc': user.firstName + ' ' + user.lastName + ' (' + (settings.AllowCriticalEditsAdmin ? user.mobile : t("hidden_demo")) + ') ' + (settings.AllowCriticalEditsAdmin ? user.email : t("hidden_demo")),
            'pushToken': user.pushToken ? user.pushToken : ''
          });
        }
      }
      setUsers(arr);
      let arrDrivers = [];
      for (let i = 0; i < userdata.users.length; i++) {
        let user = userdata.users[i];
        if ((user.usertype) && (user.usertype === 'driver') && (user.approved === true) && (user.queue === false) && (user.driverActiveStatus === true) && (user.location)) {
          if ((auth.profile.usertype === 'fleetadmin' && user.fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'customer') {
            arrDrivers.push({
              'uid': user.id,
              'location': user.location,
              'carType': user.carType,
              'fleetadmin': user.fleetadmin ? user.fleetadmin : null
            });
          }
        }
      }
      setDrivers(arrDrivers);
    }
  }, [estimatedata.estimate, userdata.users, estimateRequested, settings.AllowCriticalEditsAdmin, auth.profile.usertype, auth.profile.uid, t]);

  useEffect(() => {
    if (auth.profile.usertype && auth.profile.usertype === 'customer') {
      setUserCombo({
        'firstName': auth.profile.firstName,
        'lastName': auth.profile.lastName,
        'mobile': auth.profile.mobile,
        'email': auth.profile.email,
        'uid': auth.profile.uid,
        'pushToken': auth.profile.pushToken ? auth.profile.pushToken : ''
      })
    }
  }, [auth.profile]);

  const mobile = (userCombo && userCombo.mobile ? true : false);

  const handleGetOptions = (e) => {
    e.preventDefault();
    if ((settings && settings.imageIdApproval && auth.profile.usertype === 'customer' && auth.profile.verifyId) || auth.profile.usertype === 'admin' || (settings && !settings.imageIdApproval && auth.profile.usertype === 'customer') || auth.profile.usertype === 'fleetadmin') {
      if (!(settings && settings.disable_cash && (auth.profile.usertype === 'admin' || auth.profile.usertype === 'fleetadmin')) || auth.profile.usertype === 'customer') {
        if ((auth.profile.usertype === 'customer' && parseFloat(auth.profile.walletBalance) >= 0) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'fleetadmin') {
          setEstimateRequested(true);
          if (userCombo && pickupAddress && dropAddress && selectedCarDetails) {
            const directionService = new window.google.maps.DirectionsService();
            directionService.route(
              {
                origin: new window.google.maps.LatLng(pickupAddress.coords.lat, pickupAddress.coords.lng),
                destination: new window.google.maps.LatLng(dropAddress.coords.lat, dropAddress.coords.lng),
                travelMode: window.google.maps.TravelMode.DRIVING
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  const route = {
                    distance_in_km: (result.routes[0].legs[0].distance.value / 1000),
                    time_in_secs: result.routes[0].legs[0].duration.value,
                    polylinePoints: result.routes[0].overview_polyline
                  };
                  setTempRoute(route);
                  if (pickupAddress && dropAddress) {
                    if (bookingType === 'Book Now') {
                      if (mobile === true) {
                        if (Array.isArray(selectedCarDetails.options) || Array.isArray(selectedCarDetails.parcelTypes)) {
                          setOptionModalStatus(true);
                        } else {
                          let estimateRequest = {
                            pickup: pickupAddress,
                            drop: dropAddress,
                            carDetails: selectedCarDetails,
                            instructionData: instructionData,
                            routeDetails: route
                          };
                          dispatch(getEstimate(estimateRequest));
                        }
                      } else {
                        setCommonAlert({ open: true, msg: t('incomplete_user') });
                      }
                    } else {
                      if (bookingType === 'Book Later' && selectedDate) {
                        if (MinutesPassed(selectedDate) >= 15) {
                          if (mobile === true) {
                            if (Array.isArray(selectedCarDetails.options) || Array.isArray(selectedCarDetails.parcelTypes)) {
                              setOptionModalStatus(true);
                            } else {
                              let estimateRequest = {
                                pickup: pickupAddress,
                                drop: dropAddress,
                                carDetails: selectedCarDetails,
                                instructionData: instructionData,
                                routeDetails: route
                              };
                              dispatch(getEstimate(estimateRequest));
                            }
                          } else {
                            setCommonAlert({ open: true, msg: t('incomplete_user') });
                          }
                        } else {
                          setCommonAlert({ open: true, msg: t('past_booking_error') });
                        }
                      } else {
                        setCommonAlert({ open: true, msg: t('select_proper') });
                      }
                    }
                  } else {
                    setCommonAlert({ open: true, msg: t('place_to_coords_error') })
                  }
                }
              }
            )
          } else {
            setCommonAlert({ open: true, msg: t('select_proper') })
          }
        } else {
          setCommonAlert({ open: true, msg: t('wallet_balance_low') })
        }
      } else {
        setCommonAlert({ open: true, msg: t('cash_booking_false') })
      }
    } else {
      setCommonAlert({ open: true, msg: t('verifyid_error') })
    }
  }

  const handleWalletPayment = (e) => {
    e.preventDefault();
    let curBooking = { ...bookingdata.booking.mainData };
    if (parseFloat(curBooking.trip_cost) > parseFloat(auth.profile.walletBalance) && radioProps[payment_mode].cat === 'wallet') {
      setCommonAlert({ open: true, msg: t('wallet_balance_low') });
    } else {
      let requestedDrivers = {};
      if (settings.autoDispatch && bookingType === 'Book Now') {
        for (let i = 0; i < drivers.length; i++) {
          const driver = drivers[i];
          let distance = GetDistance(pickupAddress.coords.lat, pickupAddress.coords.lng, driver.location.lat, driver.location.lng);
          if (settings.convert_to_mile) {
            distance = distance / 1.609344;
          }
          if (distance < ((settings && settings.driverRadius) ? settings.driverRadius : 10) && selectedCarDetails.name === driver.carType) {
            requestedDrivers[driver['uid']] = true;
          }
        }
      }
      curBooking.prepaid = true;
      curBooking.status = 'NEW';
      curBooking.payment_mode = "wallet";
      curBooking.customer_paid = parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney = parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount = 0;
      curBooking.requestedDrivers = requestedDrivers;

      dispatch(updateBooking(curBooking));

      setTimeout(() => {
        dispatch(clearEstimate());
        setPaymentModalStatus(false);
        setWalletModalStatus(false);
        clearForm();
        dispatch(clearBooking());
        setTempRoute(null);
        navigate('/bookings')
      }, 1500);
    }
  }

  const handleGetEstimate = (e) => {
    e.preventDefault();
    setOptionModalStatus(false);
    // Sync parcelActive/optionActive based on booking mode
    let finalInstructionData = { ...instructionData };
    if (bookingMode === 'passenger') {
      finalInstructionData.parcelActive = false;
      finalInstructionData.parcelQuantity = 0;
    } else if (bookingMode === 'parcel') {
      finalInstructionData.optionActive = false;
      finalInstructionData.passengerCount = 0;
    }
    setInstructionData(finalInstructionData);
    let estimateRequest = {
      pickup: pickupAddress,
      drop: dropAddress,
      carDetails: selectedCarDetails,
      instructionData: finalInstructionData,
      routeDetails: tempRoute
    };
    dispatch(getEstimate(estimateRequest));
  };

  const applySelectedPromo = (item) => {
    var toDay = new Date();
    var expDate = new Date(item.promo_validity);
    expDate.setDate(expDate.getDate() + 1);
    item.usersUsed = item.usersUsed ? item.usersUsed : {};

    const completedRides = userdata?.bookings ? userdata.bookings.filter(b => b.status === 'COMPLETE' || b.status === 'PAID') : [];
    const baseFare = estimatedata.estimate ? estimatedata.estimate.estimateFare : 0;

    if (baseFare < item.min_order) {
      alert(t('promo_eligiblity') || "Sorry! Promo is not valid as bill amount is lower.");
    } else if (item.first_ride_only && completedRides.length > 0) {
      alert("This promo code is only valid for your first ride.");
    } else if (item.user_avail && item.user_avail >= item.promo_usage_limit) {
      alert(t('promo_exp_limit') || "Sorry! Promo usage limit over.");
    } else if (item.usersUsed[auth.profile.uid]) {
      alert(t('promo_used') || "Sorry! You have already used this promo code.");
    } else if (toDay > expDate) {
      alert(t('promo_exp') || "Sorry! Promo usage date has expired.");
    } else {
      let discounttype = item.promo_discount_type.toUpperCase();
      let discValue = 0;
      if (discounttype === 'PERCENTAGE') {
        discValue = parseFloat(baseFare * item.promo_discount_value / 100);
        if (discValue > item.max_promo_discount_value) {
          discValue = item.max_promo_discount_value;
        }
      } else {
        discValue = parseFloat(item.promo_discount_value);
      }
      setDiscount(parseFloat(discValue));
      setAppliedPromo(item);
      setPromoCode(item.promo_code);
    }
  };

  const handleApplyPromo = () => {
    if (!promoCode) return;
    const code = promoCode.toUpperCase();
    const allPromos = promodata?.promos || [];
    const item = allPromos.find(p => p.promo_code === code && p.promo_show);
    if (!item) {
      alert(t('promo_not_found') || "Promo code not found");
      return;
    }
    applySelectedPromo(item);
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setDiscount(0);
    setPromoCode('');
  };

  const confirmBooking = (e) => {
    e.preventDefault();
    let requestedDrivers = {};
    let driverEstimates = {};

    let notfound = true;
    if (activeBookings) {
      for (let i = 0; i < activeBookings.length; i++) {
        if (activeBookings[i].payment_mode === 'wallet' && activeBookings[i].status !== 'PAID' && activeBookings[i].status !== 'COMPLETE') {
          notfound = false;
          break;
        }
      }
    }

    if ((radioProps[payment_mode].cat === 'wallet' && notfound && auth.profile.usertype === 'customer') || radioProps[payment_mode].cat !== 'wallet' || auth.profile.usertype === 'admin' || auth.profile.usertype === 'fleetadmin') {
      const regx1 = /([0-9\s-]{7,})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
      if ((otherPerson && /\S/.test(instructionData.otherPerson) && regx1.test(instructionData.otherPersonPhone) && instructionData.otherPersonPhone && instructionData.otherPersonPhone.length > 6) || !otherPerson) {
        const fareLimit = parseFloat(estimatedata.estimate.estimateFare - discount);
        if ((radioProps[payment_mode].cat === 'wallet' && (parseFloat(auth.profile.walletBalance) >= fareLimit) && !calcEst && auth.profile.usertype === 'customer') || radioProps[payment_mode].cat !== 'wallet' || (radioProps[payment_mode].cat === 'wallet' && calcEst && auth.profile.usertype === 'customer') || auth.profile.usertype === 'admin' || auth.profile.usertype === 'fleetadmin') {
          if (settings.autoDispatch && bookingType === 'Book Now') {
            for (let i = 0; i < drivers.length; i++) {
              const driver = drivers[i];
              let distance = GetDistance(pickupAddress.coords.lat, pickupAddress.coords.lng, driver.location.lat, driver.location.lng);
              if (settings.convert_to_mile) {
                distance = distance / 1.609344;
              }
              if (distance < ((settings && settings.driverRadius) ? settings.driverRadius : 10) && ((settings.carType_required && selectedCarDetails.name === driver.carType) || !settings.carType_required) && ((auth.profile.usertype === 'fleetadmin' && driver.fleetadmin === auth.profile.uid) || auth.profile.usertype !== 'fleetadmin')) {
                requestedDrivers[driver['uid']] = true;
                driverEstimates[driver['uid']] = { distance: distance, timein_text: ((distance * 2) + 1).toFixed(0) + ' min' };
              }
            }
            if (Object.keys(requestedDrivers).length === 0) {
              setCommonAlert({ open: true, msg: t('no_driver_found_alert_messege') });
              return;
            }
          }

          let bookingObject = {
            pickup: pickupAddress,
            drop: dropAddress,
            carDetails: selectedCarDetails,
            userDetails: {
              uid: userCombo.uid,
              firstName: userCombo.firstName,
              lastName: userCombo.lastName,
              mobile: userCombo.mobile,
              pushToken: userCombo.pushToken,
              email: userCombo.email
            },
            estimate: estimatedata.estimate,
            instructionData: instructionData,
            tripInstructions: tripInstructions,
            roundTrip: roundTrip === 0 ? false : true,
            tripdate: bookingType === 'Book Later' ? new Date(selectedDate).getTime() : new Date().getTime(),
            bookLater: bookingType === 'Book Later' ? true : false,
            settings: settings,
            booking_type_admin: auth.profile.usertype === 'admin' ? true : false,
            fleetadmin: auth.profile.usertype === 'fleetadmin' ? auth.profile.uid : null,
            requestedDrivers: calcEst ? requestedDrivers : (optionsRequired && radioProps[payment_mode].cat === 'cash') ? requestedDrivers : (settings.prepaid && radioProps[payment_mode].cat === 'cash') ? requestedDrivers : !settings.prepaid ? requestedDrivers : requestedDrivers,
            driverEstimates: driverEstimates,
            payment_mode: (auth.profile.usertype === 'admin' || auth.profile.usertype === 'fleetadmin') ? 'cash' : radioProps[payment_mode].cat,
            booking_from_web: true,
            deliveryWithBid: auth.profile.usertype === 'customer' ? deliveryWithBid === 0 ? false : true : false,
            discount: discount,
            promo_applied: appliedPromo ? true : false,
            promo_details: appliedPromo ? appliedPromo : null,
          };

          if (auth.profile.usertype === 'customer' && !(auth.profile.firstName && auth.profile.lastName && auth.profile.email && auth.profile.firstName.length > 0 && auth.profile.lastName.length > 0 && auth.profile.email.length > 0)) {
            if ((profileData.firstName && profileData.firstName.length > 0 && profileData.lastName && profileData.lastName.length > 0) || (auth.profile.firstName && auth.profile.firstName.length > 0 && auth.profile.lastName && auth.profile.lastName.length > 0)) {
              const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              if ((re.test(profileData.email) && !auth.profile.email) || (auth.profile.email && auth.profile.email.length > 0)) {
                setLoading(true);
                checkUserExists({ email: profileData.email }).then(async (res) => {
                  if (res.users && res.users.length > 0) {
                    setLoading(false);
                    alert(t('user_exists'));
                  }
                  else if (res.error) {
                    setLoading(false);
                    alert(t('email_or_mobile_issue'));
                  } else {
                    setLoading(false);
                    if (radioProps[payment_mode].cat === 'card') {
                      const paymentPacket = {
                        payment_mode: 'card',
                        customer_paid: parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal),
                        cardPaymentAmount: parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal),
                        discount: discount,
                        usedWalletMoney: 0,
                        cashPaymentAmount: 0,
                        promo_applied: appliedPromo ? true : false,
                        promo_details: appliedPromo ? appliedPromo : null,
                        payableAmount: parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal),
                      };
                      bookingObject['paymentPacket'] = paymentPacket;
                    }

                    const result = validateBookingObj(t, bookingObject, instructionData, otherPerson);
                    if (result.error) {
                      setCommonAlert({ open: true, msg: result.msg });
                    } else {
                      profileData['uid'] = auth.profile.uid;
                      let bookingData = result.bookingObject;
                      bookingData.userDetails.firstName = profileData.firstName;
                      bookingData.userDetails.lastName = profileData.lastName;
                      bookingData.userDetails.email = profileData.email;
                      if (appliedPromo) {
                        let tempPromo = { ...appliedPromo };
                        tempPromo.user_avail = tempPromo.user_avail ? parseInt(tempPromo.user_avail) + 1 : 1;
                        tempPromo.usersUsed[auth.profile.uid] = true;
                        dispatch(editPromo(tempPromo, "Update"));
                      }
                      setBookingOnWait(bookingData);
                    }
                    setTimeout(() => {
                      dispatch(updateProfileWithEmail(profileData));
                    }, 200)
                  }
                });
              } else {
                setCommonAlert({ open: true, msg: t('proper_email') })
              }
            } else {
              setCommonAlert({ open: true, msg: t('proper_input_name') })
            }
          } else {
            if (radioProps[payment_mode].cat === 'card') {
              const paymentPacket = {
                payment_mode: 'card',
                customer_paid: parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal),
                cardPaymentAmount: parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal),
                discount: discount,
                usedWalletMoney: 0,
                cashPaymentAmount: 0,
                promo_applied: appliedPromo ? true : false,
                promo_details: appliedPromo ? appliedPromo : null,
                payableAmount: parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal),
              };
              bookingObject['paymentPacket'] = paymentPacket;
            }
            const result = validateBookingObj(t, bookingObject, instructionData, otherPerson);
            if (result.error) {
              setCommonAlert({ open: true, msg: result.msg });
            } else {
              if (appliedPromo) {
                let tempPromo = { ...appliedPromo };
                tempPromo.user_avail = tempPromo.user_avail ? parseInt(tempPromo.user_avail) + 1 : 1;
                tempPromo.usersUsed[auth.profile.uid] = true;
                dispatch(editPromo(tempPromo, "Update"));
              }
              setEstimateModalStatus(false);
              dispatch(addBooking(result.bookingObject));
            }
          }
        } else {
          setCommonAlert({ open: true, msg: t('wallet_balance_low') });
        }
      } else {
        setCommonAlert({ open: true, msg: t('otherPersonDetailMissing') })
      }
    } else {
      setCommonAlert({ open: true, msg: t('wallet_booking_alert') });
    }
  };

  const handleOptionModalClose = (e) => {
    e.preventDefault();
    setOptionModalStatus(false);
  };

  const handleEstimateModalClose = (e) => {
    e.preventDefault();
    setEstimateModalStatus(false);
    dispatch(clearEstimate());
    setEstimateRequested(false);
    setPromoCode('');
    setAppliedPromo(null);
    setDiscount(0);
  };

  const handleEstimateErrorClose = (e) => {
    e.preventDefault();
    dispatch(clearEstimate());
    setEstimateRequested(false);
  };

  const handleBookingErrorClose = (e) => {
    e.preventDefault();
    dispatch(clearBooking());
    setEstimateRequested(false);
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };

  const handleWalletModalClose = (e) => {
    setTimeout(() => {
      setWalletModalStatus(false);
      dispatch(clearBooking());
      dispatch(clearEstimate());
    }, 1500);
  }

  const handlePaymentModalClose = (e) => {
    setTimeout(() => {
      setPaymentModalStatus(false);
      dispatch(clearBooking());
      dispatch(clearEstimate());
    }, 1500);
  }

  // Fallback Translator Helper
  const trans = (key, fallback) => {
    const val = t(key);
    return val === key ? fallback : val;
  };

  return (
    <Box ref={rootRef} sx={{ backgroundColor: 'var(--bg-main)', minHeight: '100vh', padding: { xs: '12px 10px', sm: '16px 12px', md: '24px' }, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      
      {/* Upper header action section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: { xs: '16px', md: '24px' }, maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' }}>
        <div>
          <Typography sx={{ fontWeight: 700, color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif', fontSize: { xs: '20px', sm: '24px', md: '2.125rem' } }}>
            {t('add_booking_title')}
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: { xs: '12px', sm: '14px' } }}>
            {trans('create_new_dispatch_ride', 'Create a new dispatch ride booking instantly.')}
          </Typography>
        </div>
      </Box>

      <Box sx={{
        marginLeft: 'auto',
        marginRight: 'auto',
        backgroundColor: 'var(--bg-card)',
        borderRadius: { xs: '12px', md: '24px' },
        width: '100%',
        maxWidth: '1200px',
        boxShadow: { xs: 'none', md: 'var(--shadow)' },
        border: { xs: 'none', md: 'var(--card-border)' },
        overflow: 'hidden',
        position: 'relative',
        boxSizing: 'border-box'
      }}>
        <style>
          {`
            .MuiFormLabel-root {
              color: var(--text-secondary) !important;
            }
            .MuiInputLabel-root {
              color: var(--text-secondary) !important;
            }
            .MuiSelect-select {
              color: var(--text-main) !important;
            }
            .MuiRadio-root {
              color: var(--primary) !important;
            }
            .MuiFormControlLabel-label {
              color: var(--text-main) !important;
            }
            .MuiOutlinedInput-root {
              background-color: var(--table-input-bg) !important;
              color: var(--text-main) !important;
              border-radius: 12px !important;
            }
            .MuiOutlinedInput-notchedOutline {
              border-color: var(--border-color) !important;
              transition: border-color 0.2s ease !important;
            }
            .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
              border-color: var(--primary) !important;
            }
            .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
              border-color: var(--primary) !important;
              border-width: 2px !important;
            }
            .MuiAutocomplete-popper {
              z-index: 1300 !important;
            }
            .MuiAutocomplete-paper {
              background-color: var(--bg-card) !important;
              color: var(--text-main) !important;
              border: var(--card-border) !important;
              border-radius: 16px !important;
              box-shadow: var(--shadow-lg) !important;
              margin-top: 4px !important;
            }
            .MuiAutocomplete-option {
              color: var(--text-main) !important;
              font-size: 14px !important;
              transition: background-color 0.2s ease !important;
            }
            .MuiAutocomplete-option:hover {
              background-color: var(--table-row-selected) !important;
            }
            .MuiAutocomplete-option[aria-selected="true"] {
              background-color: rgba(72, 127, 255, 0.15) !important;
              font-weight: 600 !important;
            }
            .MuiAutocomplete-option[aria-selected="true"]:hover {
              background-color: rgba(72, 127, 255, 0.2) !important;
            }
            .MuiAutocomplete-option[data-focus="true"] {
              background-color: var(--table-row-selected) !important;
            }
            .MuiAutocomplete-noOptions {
              background-color: var(--bg-card) !important;
              color: var(--text-secondary) !important;
              padding: 12px !important;
              border-radius: 16px !important;
              border: var(--card-border) !important;
              box-shadow: var(--shadow-lg) !important;
            }
            .MuiPopover-paper, .MuiMenu-paper {
              background-color: var(--bg-card) !important;
              color: var(--text-main) !important;
              border: var(--card-border) !important;
              border-radius: 12px !important;
              box-shadow: var(--shadow-lg) !important;
            }
            .MuiMenuItem-root {
              color: var(--text-main) !important;
              font-size: 14px !important;
              transition: background-color 0.2s ease !important;
            }
            .MuiMenuItem-root:hover {
              background-color: var(--table-row-selected) !important;
            }
            .MuiMenuItem-root.Mui-selected {
              background-color: rgba(72, 127, 255, 0.15) !important;
              font-weight: 600 !important;
            }
            .MuiMenuItem-root.Mui-selected:hover {
              background-color: rgba(72, 127, 255, 0.2) !important;
            }
          `}
        </style>
        
        <Grid container>
          {/* Left Panel: Preview & Vehicle Showcase */}
          <Grid item xs={12} md={5} sx={{
            background: 'linear-gradient(135deg, rgba(72, 127, 255, 0.06) 0%, rgba(72, 127, 255, 0.01) 100%)',
            borderRight: {
              xs: 'none',
              md: isRTL === 'rtl' ? 'none' : '1px solid var(--border-color)'
            },
            borderLeft: {
              xs: 'none',
              md: isRTL === 'rtl' ? '1px solid var(--border-color)' : 'none'
            },
            borderBottom: {
              xs: '1px solid var(--border-color)',
              md: 'none'
            },
            padding: {
              xs: '16px 12px',
              sm: '20px 16px',
              md: '32px'
            },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box'
          }}>
            <div>
              <Typography sx={{ fontWeight: 700, color: 'var(--text-main)', marginBottom: { xs: '12px', md: '20px' }, fontFamily: 'Outfit, sans-serif', fontSize: { xs: '16px', md: '1.25rem' } }}>
                {trans('trip_preview', 'Trip Preview')}
              </Typography>

              {/* Route Timeline Graphic */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', background: 'var(--bg-card)', padding: '14px', borderRadius: '12px', border: 'var(--card-border)', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    border: '2px solid var(--bg-card)',
                    boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
                    zIndex: 2
                  }} />
                  <div style={{
                    width: '2px',
                    flexGrow: 1,
                    backgroundColor: 'var(--border-color)',
                    margin: '6px 0',
                    zIndex: 1,
                    minHeight: '40px'
                  }} />
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '2px',
                    backgroundColor: '#ef4444',
                    border: '2px solid var(--bg-card)',
                    boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)',
                    zIndex: 2
                  }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '12px', flexGrow: 1 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '9px', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.05em' }}>
                      {trans('pickup', 'Pickup Location')}
                    </span>
                    <span style={{ fontSize: '13px', color: pickupAddress ? 'var(--text-main)' : 'var(--text-secondary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                      {pickupAddress ? pickupAddress.description : trans('enter_pickup', 'Select Pickup Address')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '9px', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.05em' }}>
                      {trans('dropoff', 'Dropoff Location')}
                    </span>
                    <span style={{ fontSize: '13px', color: dropAddress ? 'var(--text-main)' : 'var(--text-secondary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                      {dropAddress ? dropAddress.description : trans('enter_dropoff', 'Select Dropoff Address')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Selected Vehicle Showcase */}
              <Box sx={{
                background: 'var(--bg-card)',
                border: 'var(--card-border)',
                borderRadius: { xs: '12px', md: '16px' },
                padding: { xs: '16px', md: '24px' },
                boxShadow: 'var(--shadow-sm)',
                textAlign: 'center',
                marginBottom: { xs: '12px', md: '20px' },
                transition: 'all 0.3s ease'
              }}>
                {selectedCarDetails ? (
                  <>
                    <img
                      src={selectedCarDetails.image}
                      alt={selectedCarDetails.name}
                      style={{ height: '90px', objectFit: 'contain', marginBottom: '16px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
                    />
                    <Typography style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif' }}>
                      {selectedCarDetails.name}
                    </Typography>
                    <Typography style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      {trans('premium_service', 'Premium Dispatch Vehicle')}
                    </Typography>
                  </>
                ) : (
                  <div style={{ padding: '20px 0' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--table-input-bg)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px auto',
                      color: 'var(--text-secondary)'
                    }}>
                      <DirectionsCarIcon style={{ fontSize: '32px' }} />
                    </div>
                    <Typography style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {trans('select_vehicle_preview', 'Select a vehicle to preview')}
                    </Typography>
                  </div>
                )}
              </Box>
            </div>

            {/* Trip Booking Mode Status Summary */}
            <Box sx={{
              background: 'var(--primary)',
              borderRadius: { xs: '10px', md: '16px' },
              padding: { xs: '12px 14px', md: '16px 20px' },
              color: 'white',
              boxShadow: '0 4px 14px rgba(72, 127, 255, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: { xs: '8px', md: '20px' }
            }}>
              <div>
                <span style={{ fontSize: '9px', textTransform: 'uppercase', opacity: 0.8, fontWeight: 700, letterSpacing: '0.05em' }}>
                  {trans('booking_mode', 'Booking Mode')}
                </span>
                <Typography style={{ fontSize: '14px', fontWeight: 700 }}>
                  {bookingType === 'Book Now' ? trans('book_now_label', 'Immediate Dispatch') : trans('book_later_label', 'Scheduled Ride')}
                </Typography>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 700
              }}>
                {bookingType === 'Book Now' ? trans('now', 'NOW') : moment(selectedDate).format('MMM D, h:mm a')}
              </div>
            </Box>
          </Grid>

          {/* Right Panel: Form Inputs */}
          <Grid item xs={12} md={7} sx={{
            padding: {
              xs: '16px 12px',
              sm: '20px 16px',
              md: '32px'
            },
            boxSizing: 'border-box'
          }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--text-main)', marginBottom: { xs: '12px', md: '20px' }, fontFamily: 'Outfit, sans-serif', fontSize: { xs: '16px', md: '1.25rem' } }}>
              {trans('booking_details', 'Booking Details')}
            </Typography>

            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {/* Select User (if admin) */}
              {users && auth.profile.usertype && auth.profile.usertype !== 'customer' && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    {trans('select_customer', 'Customer Account')}
                  </Typography>
                  <UsersCombo
                    className={classes.items}
                    placeholder={t('select_user')}
                    users={users}
                    value={userCombo}
                    onChange={(event, newValue) => {
                      setUserCombo(newValue);
                    }}
                  />
                </Grid>
              )}

              {/* Pickup Location */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {trans('pickup_location_label', 'Pickup Address')}
                </Typography>
                <GoogleMapsAutoComplete
                  variant={"outlined"}
                  placeholder={t('pickup_location')}
                  value={pickupAddress}
                  style={{ width: '100%' }}
                  onChange={(value) => setPickupAddress(value)}
                />
              </Grid>

              {/* Drop Location */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {trans('drop_location_label', 'Dropoff Address')}
                </Typography>
                <GoogleMapsAutoComplete
                  placeholder={t('drop_location')}
                  variant={"outlined"}
                  value={dropAddress}
                  style={{ width: '100%' }}
                  onChange={(value) => setDropAddress(value)}
                />
              </Grid>

              {/* Vehicle Type Selector (Horizontal Cards) */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {trans('select_vehicle_label', 'Select Vehicle Service')}
                </Typography>
                {cartypes ? (
                  <Box sx={{
                    display: 'flex',
                    gap: { xs: '8px', sm: '10px', md: '12px' },
                    overflowX: 'auto',
                    padding: '4px 0 12px 0',
                    scrollbarWidth: 'thin',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    justifyContent: { xs: 'flex-start', md: 'flex-start' },
                    WebkitOverflowScrolling: 'touch',
                  }}>
                    {cartypes.sort((a, b) => a.pos - b.pos).map((car) => {
                      const isSelected = car.name === carType;
                      return (
                        <Box
                          key={car.name}
                          onClick={() => handleCarCardClick(car)}
                          sx={{
                            flex: { xs: '0 0 calc(25% - 6px)', sm: '0 0 calc(20% - 8px)', md: '0 0 110px' },
                            minWidth: { xs: '70px', sm: '80px', md: '110px' },
                            maxWidth: { xs: '90px', sm: '100px', md: '110px' },
                            background: isSelected ? 'rgba(72, 127, 255, 0.08)' : 'var(--bg-card)',
                            border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border-color)',
                            borderRadius: { xs: '10px', md: '16px' },
                            padding: { xs: '10px 6px', sm: '12px 8px', md: '16px 12px' },
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: isSelected ? '0 4px 12px rgba(72, 127, 255, 0.15)' : 'none',
                            position: 'relative',
                            boxSizing: 'border-box',
                            '&:active': {
                              transform: 'scale(0.95)',
                            },
                          }}
                        >
                          <img src={car.image} alt={car.name} style={{ height: '30px', objectFit: 'contain', marginBottom: '6px' }} />
                          <Typography sx={{ fontSize: { xs: '9px', sm: '10px', md: '11px' }, fontWeight: 700, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {car.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading car types...</Typography>
                )}
              </Grid>

              {/* Booking Type Pill Switch */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {trans('dispatch_schedule', 'Scheduling Mode')}
                </Typography>
                <Box sx={{ display: 'flex', background: 'var(--table-input-bg)', padding: '3px', borderRadius: '10px', border: 'var(--card-border)' }}>
                  <Button
                    onClick={() => { setBookingType('Book Now'); }}
                    variant={bookingType === 'Book Now' ? 'contained' : 'text'}
                    sx={{
                      flex: 1,
                      textTransform: 'none',
                      borderRadius: '8px',
                      padding: { xs: '8px 10px', md: '10px 16px' },
                      fontWeight: 600,
                      fontSize: { xs: '13px', md: '14px' },
                      minHeight: { xs: '40px', md: '44px' },
                      backgroundColor: bookingType === 'Book Now' ? 'var(--primary)' : 'transparent',
                      color: bookingType === 'Book Now' ? '#000000' : 'var(--text-secondary)',
                      boxShadow: bookingType === 'Book Now' ? '0 4px 12px rgba(233, 174, 49, 0.25)' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: bookingType === 'Book Now' ? 'var(--primary)' : 'rgba(233, 174, 49, 0.08)',
                      },
                    }}
                  >
                    {t('book_now')}
                  </Button>
                  <Button
                    onClick={() => { setBookingType('Book Later'); setSelectedDate(roundToNearest15(moment())); }}
                    variant={bookingType === 'Book Later' ? 'contained' : 'text'}
                    sx={{
                      flex: 1,
                      textTransform: 'none',
                      borderRadius: '8px',
                      padding: { xs: '8px 10px', md: '10px 16px' },
                      fontWeight: 600,
                      fontSize: { xs: '13px', md: '14px' },
                      minHeight: { xs: '40px', md: '44px' },
                      backgroundColor: bookingType === 'Book Later' ? 'var(--primary)' : 'transparent',
                      color: bookingType === 'Book Later' ? '#000000' : 'var(--text-secondary)',
                      boxShadow: bookingType === 'Book Later' ? '0 4px 12px rgba(233, 174, 49, 0.25)' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: bookingType === 'Book Later' ? 'var(--primary)' : 'rgba(233, 174, 49, 0.08)',
                      },
                    }}
                  >
                    {t('book_later')}
                  </Button>
                </Box>
              </Grid>

              {/* Scheduled Date/Time Input */}
              {bookingType === 'Book Later' && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        {t('booking_date', 'Booking Date')}
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          label=""
                          value={moment(selectedDate)}
                          onChange={handleDatePickerChange}
                          minDate={moment()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              fullWidth
                              className={[isRTL === 'rtl' ? classes.inputRtl : classes.commonInputStyle, classes.textField].join(" ")}
                              InputProps={{
                                ...params.InputProps,
                                className: classes.input,
                                style: { textAlignLast: isRTL === 'rtl' ? 'end' : 'start', color: 'var(--text-main)' }
                              }}
                              InputLabelProps={{
                                ...params.InputLabelProps,
                                style: { color: 'var(--text-secondary)' }
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        {t('booking_time', 'Booking Time')}
                      </Typography>
                      <FormControl fullWidth variant="outlined">
                        <Select
                          id="booking-time"
                          value={moment(selectedDate).format('HH:mm')}
                          onChange={(e) => {
                            const parsedTime = moment(e.target.value, 'HH:mm');
                            const newDateTime = moment(selectedDate)
                              .hours(parsedTime.hours())
                              .minutes(parsedTime.minutes());
                            setSelectedDate(roundToNearest15(newDateTime));
                          }}
                          sx={{
                            color: 'var(--text-main)',
                            backgroundColor: 'transparent',
                            height: '56px',
                            borderRadius: '8px',
                            '.MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--border-color)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--primary)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--primary)',
                            },
                            '.MuiSvgIcon-root': {
                              color: 'var(--text-secondary)',
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: 'var(--bg-card)',
                                color: 'var(--text-main)',
                                maxHeight: 250,
                              }
                            }
                          }}
                        >
                          {(() => {
                            const slots = [];
                            const start = moment().startOf('day');
                            for (let i = 0; i < 96; i++) {
                              slots.push(start.format('HH:mm'));
                              start.add(15, 'minutes');
                            }
                            return slots.map((slot) => (
                              <MenuItem key={slot} value={slot} sx={{ '&:hover': { backgroundColor: 'rgba(233, 174, 49, 0.08)' }, '&.Mui-selected': { backgroundColor: 'var(--primary) !important', color: '#08080A' } }}>
                                {moment(slot, 'HH:mm').format('hh:mm A')}
                              </MenuItem>
                            ));
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Action dispatch Button */}
              <Grid item xs={12} sx={{ marginTop: { xs: '12px', md: '24px' } }}>
                <Button
                  onClick={handleGetOptions}
                  variant="contained"
                  sx={{
                    width: '100%',
                    height: { xs: '46px', md: '52px' },
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                    color: '#000000',
                    fontSize: { xs: '14px', md: '16px' },
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(233, 174, 49, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 20px rgba(233, 174, 49, 0.4)',
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  <DirectionsCarIcon style={{ fontSize: '20px' }} />
                  {t('book')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Modals section */}
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={optionModalStatus}
        onClose={handleOptionModalClose}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid container spacing={2} className={classes.paper}>
          {/* Modal Header */}
          <Grid item xs={12} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', paddingBottom: 0 }}>
            <Typography style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '4px' }}>
              {t('customize_booking', 'Customize Your Booking')}
            </Typography>
            <Typography style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {t('select_booking_options', 'Choose what you want to book for this trip')}
            </Typography>
          </Grid>

          {/* Booking Mode Tab Selector — only show when BOTH parcelTypes exist AND it is a bus/van */}
          {selectedCarDetails && selectedCarDetails.parcelTypes && selectedCarDetails.parcelTypes.length > 0 && isBusOrVan ? (
            <Grid item xs={12} style={{ paddingTop: '12px', paddingBottom: 0 }}>
              <Box sx={{
                display: 'flex',
                gap: '8px',
                padding: '4px',
                borderRadius: '14px',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
              }}>
                {[
                  { key: 'passenger', label: t('passenger', 'Passenger'), icon: <PeopleIcon style={{ fontSize: '18px' }} /> },
                  { key: 'parcel', label: t('parcel', 'Parcel'), icon: <Inventory2Icon style={{ fontSize: '18px' }} /> },
                  { key: 'both', label: t('both', 'Both'), icon: <AppsIcon style={{ fontSize: '18px' }} /> },
                ].map((tab) => (
                  <Box
                    key={tab.key}
                    onClick={() => {
                      setBookingMode(tab.key);
                      // Sync active states with the selected mode
                      let updatedData = { ...instructionData };
                      if (tab.key === 'passenger') {
                        updatedData.optionActive = true;
                        updatedData.passengerCount = updatedData.passengerCount || 1;
                        updatedData.parcelActive = false;
                        updatedData.parcelQuantity = 0;
                      } else if (tab.key === 'parcel') {
                        updatedData.parcelActive = true;
                        updatedData.parcelQuantity = updatedData.parcelQuantity || 1;
                        updatedData.optionActive = false;
                        updatedData.passengerCount = 0;
                      } else {
                        updatedData.parcelActive = true;
                        updatedData.optionActive = true;
                        updatedData.parcelQuantity = updatedData.parcelQuantity || 1;
                        updatedData.passengerCount = updatedData.passengerCount || 1;
                      }
                      setInstructionData(updatedData);
                    }}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '10px 8px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      fontWeight: bookingMode === tab.key ? 700 : 500,
                      fontSize: '13px',
                      color: bookingMode === tab.key ? '#08080A' : 'var(--text-secondary)',
                      background: bookingMode === tab.key
                        ? 'linear-gradient(135deg, #E9AE31 0%, #c58d1b 100%)'
                        : 'transparent',
                      boxShadow: bookingMode === tab.key
                        ? '0 2px 8px rgba(233, 174, 49, 0.3)'
                        : 'none',
                      '&:hover': {
                        backgroundColor: bookingMode === tab.key
                          ? undefined
                          : 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                  </Box>
                ))}
              </Box>
            </Grid>
          ) : null}

          {/* ━━━━━━━━━━━ PASSENGER SECTION ━━━━━━━━━━━ */}
          {selectedCarDetails && isBusOrVan && (bookingMode === 'passenger' || bookingMode === 'both') ? (
            <Grid item xs={12} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
              <Box sx={{
                width: '100%',
                border: '1px solid rgba(99, 179, 237, 0.2)',
                borderRadius: '16px',
                padding: '18px',
                backgroundColor: 'rgba(99, 179, 237, 0.03)',
                position: 'relative',
              }}>
                {/* Section Header */}
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(99, 179, 237, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <PeopleIcon style={{ fontSize: '20px', color: '#63B3ED' }} />
                    </Box>
                    <Box>
                      <Typography style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-main)', lineHeight: 1.2 }}>
                        {t('passenger_booking', 'Passenger Booking')}
                      </Typography>
                      <Typography style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {t('select_seat_type', 'Select seat type and number of passengers')}
                      </Typography>
                    </Box>
                  </Box>
                  {bookingMode === 'both' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instructionData.optionActive}
                          onChange={(e) => {
                            const active = e.target.checked;
                            setInstructionData({
                              ...instructionData,
                              optionActive: active,
                              passengerCount: active ? 1 : 0
                            });
                          }}
                          sx={{
                            color: 'rgba(99, 179, 237, 0.4)',
                            '&.Mui-checked': {
                              color: '#63B3ED',
                            }
                          }}
                        />
                      }
                      label={<Typography style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('include', 'Include')}</Typography>}
                    />
                  )}
                </Box>

                {/* Option Cards */}
                {selectedCarDetails.options && selectedCarDetails.options.length > 0 && selectedCarDetails.options.map((element, index) => {
                  const isSelected = instructionData.optionIndex === index && instructionData.optionActive;
                  const descParts = element.description.split(" - ");
                  const title = descParts[0] || "";
                  const detail = descParts.slice(1).join(" - ") || "";
                  const price = settings.swipe_symbol === false
                    ? `${settings.symbol} ${element.amount}`
                    : `${element.amount} ${settings.symbol}`;

                  return (
                    <Box
                      key={element.description}
                      onClick={() => {
                        setInstructionData({
                          ...instructionData,
                          optionActive: true,
                          optionIndex: index,
                          optionSelected: element,
                          passengerCount: instructionData.passengerCount === 0 ? 1 : instructionData.passengerCount
                        });
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '12px 14px',
                        marginBottom: '8px',
                        borderRadius: '10px',
                        opacity: instructionData.optionActive ? 1 : 0.4,
                        border: isSelected
                          ? '2px solid #63B3ED'
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        backgroundColor: isSelected
                          ? 'rgba(99, 179, 237, 0.08)'
                          : 'rgba(255, 255, 255, 0.02)',
                        cursor: instructionData.optionActive ? 'pointer' : 'default',
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isSelected
                          ? '0 2px 8px rgba(99, 179, 237, 0.15)'
                          : 'none',
                        pointerEvents: instructionData.optionActive ? 'auto' : 'none',
                        '&:hover': {
                          borderColor: isSelected ? '#63B3ED' : 'rgba(99, 179, 237, 0.4)',
                          backgroundColor: isSelected ? 'rgba(99, 179, 237, 0.12)' : 'rgba(255, 255, 255, 0.04)'
                        }
                      }}
                    >
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: detail ? '4px' : '0px' }}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Radio
                            checked={isSelected}
                            disabled={!instructionData.optionActive}
                            value={index}
                            sx={{
                              color: 'rgba(255, 255, 255, 0.3)',
                              '&.Mui-checked': {
                                color: '#63B3ED',
                              },
                              padding: 0
                            }}
                          />
                          <Typography style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-main)' }}>
                            {title}
                          </Typography>
                        </Box>
                        <Typography style={{ fontWeight: 700, fontSize: '15px', color: '#63B3ED' }}>
                          {price}
                        </Typography>
                      </Box>
                      {detail && (
                        <Typography style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '24px', lineHeight: 1.4 }}>
                          {detail}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {/* Passenger Count Control */}
                {instructionData.optionActive && (
                  <Box style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(99, 179, 237, 0.15)',
                    backgroundColor: 'rgba(99, 179, 237, 0.04)'
                  }}>
                    <Typography style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-main)' }}>
                      {t('number_of_passengers', 'Number of Passengers')}
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <IconButton
                        disabled={instructionData.passengerCount <= 1}
                        onClick={() => {
                          setInstructionData({
                            ...instructionData,
                            passengerCount: Math.max(1, instructionData.passengerCount - 1)
                          });
                        }}
                        size="small"
                        sx={{ color: '#63B3ED', border: '1px solid #63B3ED', '&.Mui-disabled': { color: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.1)' } }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-main)', minWidth: '20px', textAlign: 'center' }}>
                        {instructionData.passengerCount}
                      </Typography>
                      <IconButton
                        onClick={() => {
                          setInstructionData({
                            ...instructionData,
                            passengerCount: instructionData.passengerCount + 1
                          });
                        }}
                        size="small"
                        sx={{ color: '#63B3ED', border: '1px solid #63B3ED' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          ) : null}

          {/* ━━━━━━━━━━━ PARCEL SECTION ━━━━━━━━━━━ */}
          {selectedCarDetails && selectedCarDetails.parcelTypes && selectedCarDetails.parcelTypes.length > 0 && (bookingMode === 'parcel' || bookingMode === 'both') ? (
            <Grid item xs={12} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
              <Box sx={{
                width: '100%',
                border: '1px solid rgba(233, 174, 49, 0.2)',
                borderRadius: '16px',
                padding: '18px',
                backgroundColor: 'rgba(233, 174, 49, 0.03)',
                position: 'relative',
              }}>
                {/* Section Header */}
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(233, 174, 49, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Inventory2Icon style={{ fontSize: '20px', color: '#E9AE31' }} />
                    </Box>
                    <Box>
                      <Typography style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-main)', lineHeight: 1.2 }}>
                        {t('parcel_booking', 'Parcel Booking')}
                      </Typography>
                      <Typography style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {t('select_parcel_type', 'Select parcel type and quantity')}
                      </Typography>
                    </Box>
                  </Box>
                  {bookingMode === 'both' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instructionData.parcelActive}
                          onChange={(e) => {
                            const active = e.target.checked;
                            setInstructionData({
                              ...instructionData,
                              parcelActive: active,
                              parcelQuantity: active ? 1 : 0
                            });
                          }}
                          sx={{
                            color: 'rgba(233, 174, 49, 0.4)',
                            '&.Mui-checked': {
                              color: '#E9AE31',
                            }
                          }}
                        />
                      }
                      label={<Typography style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('include', 'Include')}</Typography>}
                    />
                  )}
                </Box>

                {/* Parcel Type Cards */}
                {selectedCarDetails.parcelTypes.map((element, index) => {
                  const isSelected = instructionData.parcelTypeIndex === index && instructionData.parcelActive;
                  const descParts = element.description.split(" - ");
                  const title = descParts[0] || "";
                  const detail = descParts.slice(1).join(" - ") || "";
                  const price = settings.swipe_symbol === false
                    ? `${settings.symbol} ${element.amount}`
                    : `${element.amount} ${settings.symbol}`;

                  return (
                    <Box
                      key={element.description}
                      onClick={() => {
                        setInstructionData({
                          ...instructionData,
                          parcelActive: true,
                          parcelTypeIndex: index,
                          parcelTypeSelected: element,
                          parcelQuantity: instructionData.parcelQuantity === 0 ? 1 : instructionData.parcelQuantity
                        });
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '12px 14px',
                        marginBottom: '8px',
                        borderRadius: '10px',
                        opacity: instructionData.parcelActive ? 1 : 0.4,
                        border: isSelected
                          ? '2px solid #E9AE31'
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        backgroundColor: isSelected
                          ? 'rgba(233, 174, 49, 0.08)'
                          : 'rgba(255, 255, 255, 0.02)',
                        cursor: instructionData.parcelActive ? 'pointer' : 'default',
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isSelected
                          ? '0 2px 8px rgba(233, 174, 49, 0.15)'
                          : 'none',
                        pointerEvents: instructionData.parcelActive ? 'auto' : 'none',
                        '&:hover': {
                          borderColor: isSelected ? '#E9AE31' : 'rgba(233, 174, 49, 0.5)',
                          backgroundColor: isSelected ? 'rgba(233, 174, 49, 0.12)' : 'rgba(255, 255, 255, 0.04)'
                        }
                      }}
                    >
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: detail ? '4px' : '0px' }}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Radio
                            checked={isSelected}
                            disabled={!instructionData.parcelActive}
                            value={index}
                            sx={{
                              color: 'rgba(255, 255, 255, 0.3)',
                              '&.Mui-checked': {
                                color: '#E9AE31',
                              },
                              padding: 0
                            }}
                          />
                          <Typography style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-main)' }}>
                            {title}
                          </Typography>
                        </Box>
                        <Typography style={{ fontWeight: 700, fontSize: '15px', color: '#E9AE31' }}>
                          {price}
                        </Typography>
                      </Box>
                      {detail && (
                        <Typography style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '24px', lineHeight: 1.4 }}>
                          {detail}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {/* Parcel Quantity Control */}
                {instructionData.parcelActive && (
                  <Box style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(233, 174, 49, 0.15)',
                    backgroundColor: 'rgba(233, 174, 49, 0.04)'
                  }}>
                    <Typography style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-main)' }}>
                      {t('parcel_quantity', 'Parcel Quantity')}
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <IconButton
                        disabled={instructionData.parcelQuantity <= 1}
                        onClick={() => {
                          setInstructionData({
                            ...instructionData,
                            parcelQuantity: Math.max(1, instructionData.parcelQuantity - 1)
                          });
                        }}
                        size="small"
                        sx={{ color: '#E9AE31', border: '1px solid #E9AE31', '&.Mui-disabled': { color: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.1)' } }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-main)', minWidth: '20px', textAlign: 'center' }}>
                        {instructionData.parcelQuantity}
                      </Typography>
                      <IconButton
                        onClick={() => {
                          setInstructionData({
                            ...instructionData,
                            parcelQuantity: instructionData.parcelQuantity + 1
                          });
                        }}
                        size="small"
                        sx={{ color: '#E9AE31', border: '1px solid #E9AE31' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          ) : null}

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: '10px', justifyContent: { xs: 'stretch', sm: 'flex-end' }, flexDirection: { xs: 'column-reverse', sm: 'row' }, marginTop: '16px' }}>
            <Button onClick={handleOptionModalClose} variant="outlined" className={classes.buttonStyle2} sx={{ minHeight: { xs: '44px', sm: 'auto' }, fontSize: { xs: '14px', sm: 'inherit' } }}>
              {t('cancel')}
            </Button>
            <Button
              disabled={
                (bookingMode === 'both' && !instructionData.parcelActive && !instructionData.optionActive) ||
                (bookingMode === 'passenger' && !instructionData.optionActive) ||
                (bookingMode === 'parcel' && !instructionData.parcelActive)
              }
              onClick={handleGetEstimate}
              variant="contained"
              className={classes.buttonStyle1}
              sx={{ minHeight: { xs: '44px', sm: 'auto' }, fontSize: { xs: '14px', sm: 'inherit' } }}
            >
              {t('get_estimate')}
            </Button>
          </Grid>
        </Grid>
      </Modal>

      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={estimateModalStatus}
        onClose={handleEstimateModalClose}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid container spacing={1.5} className={classes.paper}>
          <BookingModalBody
            classes={classes}
            instructionData={instructionData}
            handleChange={handleChange}
            tripInstructions={tripInstructions}
            roundTrip={roundTrip}
            auth={auth}
            profileData={profileData}
            settings={settings}
            deliveryWithBid={deliveryWithBid}
            otherPerson={otherPerson}
            setOtherPerson={setOtherPerson}
          />
          {auth.profile.usertype === 'customer' && (
            <Grid item xs={12}>
              <Box style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}>
                <Typography style={{ color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '8px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('promo_code')}
                </Typography>
                {!appliedPromo ? (
                  <Box style={{ display: 'flex', gap: '8px', width: '100%' }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder={t('enter_promo_code') || "Enter Promo Code"}
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      inputProps={{ style: { textTransform: 'uppercase', color: 'var(--text-main)' } }}
                      sx={{
                        flexGrow: 1,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "var(--border-color)" },
                          "&:hover fieldset": { borderColor: "var(--border-color)" },
                          "&.Mui-focused fieldset": { borderColor: "var(--primary)" },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleApplyPromo}
                      style={{
                        background: 'linear-gradient(135deg, #E9AE31 0%, #c58d1b 100%)',
                        color: '#08080A',
                        fontWeight: '700',
                        textTransform: 'none',
                        borderRadius: '8px',
                        padding: '0 16px',
                      }}
                    >
                      {t('apply') || "Apply"}
                    </Button>
                  </Box>
                ) : (
                  <Box 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '10px 14px', 
                      borderRadius: '10px', 
                      backgroundColor: 'rgba(21, 128, 61, 0.1)', 
                      border: '1px solid rgba(21, 128, 61, 0.3)' 
                    }}
                  >
                    <Box>
                      <Typography style={{ fontWeight: '700', fontSize: '14px', color: '#15803d' }}>
                        {appliedPromo.promo_code}
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {appliedPromo.promo_description}
                      </Typography>
                    </Box>
                    <Button 
                      onClick={handleRemovePromo} 
                      size="small" 
                      style={{ color: '#ef4444', textTransform: 'none', fontWeight: 'bold' }}
                    >
                      {t('remove') || "Remove"}
                    </Button>
                  </Box>
                )}
                {!appliedPromo && promodata?.promos && promodata.promos.filter(item => item.promo_show).length > 0 && (
                  <Box style={{ marginTop: '12px', width: '100%' }}>
                    <Typography style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('available_coupons') || "Available Coupons"}
                    </Typography>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '120px', overflowY: 'auto', paddingRight: '4px' }}>
                      {promodata.promos.filter(item => item.promo_show).map((item) => (
                        <Box 
                          key={item.promo_code}
                          onClick={() => applySelectedPromo(item)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '1px dashed var(--primary)',
                            backgroundColor: 'rgba(233, 174, 49, 0.02)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(233, 174, 49, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(233, 174, 49, 0.02)';
                          }}
                        >
                          <Box>
                            <Typography style={{ fontWeight: '700', fontSize: '12px', color: '#E9AE31' }}>
                              {item.promo_code}
                            </Typography>
                            <Typography style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                              {item.promo_description}
                            </Typography>
                          </Box>
                          <Typography style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--primary)', textTransform: 'uppercase' }}>
                            {t('use_code') || "Use Code"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          {showEst && estimatedata.estimate ? (
            <Grid item xs={12} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', margin: '12px 0' }}>
              <Box display="flex" flexDirection="column" gap="4px">
                {discount > 0 && (
                  <Typography style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>
                    {t('original_total') || "Original Total"}: {settings.swipe_symbol === false ? `${settings.symbol} ${parseFloat(estimatedata.estimate.estimateFare).toFixed(settings.decimal)}` : `${parseFloat(estimatedata.estimate.estimateFare).toFixed(settings.decimal)} ${settings.symbol}`}
                  </Typography>
                )}
                <Typography color={'primary'} style={{ fontSize: 26, fontWeight: 700 }}>
                  {t('total')} - {settings.swipe_symbol === false ? `${settings.symbol} ${parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal)}` : `${parseFloat(estimatedata.estimate.estimateFare - discount).toFixed(settings.decimal)} ${settings.symbol}`}
                </Typography>
                {discount > 0 && (
                  <Typography style={{ fontSize: '13px', color: '#15803d', fontWeight: 'bold' }}>
                    {t('discount_applied') || "Discount Applied"}: {settings.swipe_symbol === false ? `${settings.symbol} ${discount.toFixed(settings.decimal)}` : `${discount.toFixed(settings.decimal)} ${settings.symbol}`}
                  </Typography>
                )}
              </Box>
            </Grid>
          ) : null}

          {auth.profile.usertype === 'customer' ? (
            <Grid item xs={12} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', marginBottom: '16px' }}>
              <Box style={{ width: '100%', marginTop: '8px' }}>
                <FormLabel component="legend" style={{ color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '10px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('payment_mode')}</FormLabel>
                <Grid container spacing={2}>
                  {radioProps.map((element, index) => {
                    const isSelected = parseInt(payment_mode) === index;
                    return (
                      <Grid item xs={4} key={element.cat}>
                        <Box
                          onClick={() => handleChange({ target: { name: 'payment_mode', value: index } })}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '12px 8px',
                            borderRadius: '12px',
                            border: isSelected ? '2px solid #E9AE31' : '1px solid var(--border-color)',
                            backgroundColor: isSelected ? 'rgba(233, 174, 49, 0.05)' : 'var(--bg-main)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                          }}
                        >
                          <Typography style={{ fontWeight: '700', fontSize: '13px', color: isSelected ? '#E9AE31' : 'var(--text-main)' }}>
                            {element.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={12} sx={{ display: 'flex', gap: '10px', justifyContent: { xs: 'stretch', sm: 'flex-end' }, flexDirection: { xs: 'column-reverse', sm: 'row' }, width: '100%', marginTop: '16px' }}>
            {loading ? (
              <DialogActions style={{ justifyContent: 'center', alignContent: 'center', width: '100%' }}>
                <CircularProgress />
              </DialogActions>
            ) : (
              <>
                <Button onClick={handleEstimateModalClose} variant="outlined" className={classes.buttonStyle2} sx={{ minHeight: { xs: '44px', sm: 'auto' }, fontSize: { xs: '14px', sm: 'inherit' } }}>
                  {t('cancel')}
                </Button>
                <Button onClick={confirmBooking} variant="contained" className={classes.buttonStyle1} sx={{ minHeight: { xs: '44px', sm: 'auto' }, fontSize: { xs: '14px', sm: 'inherit' } }}>
                  {t('book_now')}
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Modal>

      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={walletModalStatus}
        onClose={handleWalletModalClose}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid container spacing={2} className={classes.paper}>
          <Grid item xs={12} style={{ marginBottom: '20px' }}>
            <Typography style={{ fontWeight: '700', fontSize: '20px', fontFamily: 'Outfit, sans-serif', marginBottom: 10 }}>
              {t('payment')}
            </Typography>
            {bookingdata && bookingdata.booking ? (
              <Typography color={"primary"} style={{ fontSize: 32, fontWeight: 700, margin: '12px 0' }}>
                {(settings.swipe_symbol === false ? settings.symbol + bookingdata.booking.mainData.trip_cost : bookingdata.booking.mainData.trip_cost + settings.symbol)}
              </Typography>
            ) : null}
            <Typography style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              {t('use_wallet_balance') + " (" + (settings.swipe_symbol === false ? settings.symbol + auth.profile.walletBalance : auth.profile.walletBalance + settings.symbol) + ")"}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', gap: '10px', flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
            <Button onClick={handleWalletModalClose} variant="outlined" className={classes.buttonStyle2} sx={{ minHeight: { xs: '44px', sm: 'auto' }, fontSize: { xs: '14px', sm: 'inherit' }, flex: { xs: 1, sm: 'none' } }}>
              {t('cancel')}
            </Button>
            <Button variant="contained" className={classes.buttonStyle1} onClick={handleWalletPayment} sx={{ minHeight: { xs: '44px', sm: 'auto' }, fontSize: { xs: '14px', sm: 'inherit' }, flex: { xs: 1, sm: 'none' } }}>
              {t('paynow_button')}
            </Button>
          </Grid>
        </Grid>
      </Modal>

      <PaymentGatewayModal
        open={paymentModalStatus}
        onClose={handlePaymentModalClose}
        orderId={bookingdata && bookingdata.booking ? bookingdata.booking.booking_id : ""}
        amount={bookingdata && bookingdata.booking ? bookingdata.booking.mainData.trip_cost : 0}
        currency={settings?.code}
        productName={t('bookingPayment')}
        booking={bookingdata && bookingdata.booking ? bookingdata.booking.mainData : null}
      />

      <AlertDialog open={bookingdata.error.flag} onClose={handleBookingErrorClose}>{bookingdata.error.msg}</AlertDialog>
      <AlertDialog open={estimatedata.error.flag} onClose={handleEstimateErrorClose}>{estimatedata.error.msg}</AlertDialog>
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>

      {/* Rating / Review Dialog */}
      <Dialog
        open={openRatingDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setOpenRatingDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-main)',
            border: 'var(--card-border)',
            borderRadius: { xs: '16px 16px 0 0', sm: '16px' },
            padding: { xs: '12px', sm: '16px' },
            boxShadow: 'var(--shadow)',
            margin: { xs: '0', sm: '32px' },
            position: { xs: 'fixed', sm: 'relative' },
            bottom: { xs: 0, sm: 'auto' },
            left: { xs: 0, sm: 'auto' },
            right: { xs: 0, sm: 'auto' },
            maxHeight: { xs: '85vh', sm: 'none' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: '444px' },
          }
        }}
      >
        <DialogTitle style={{ fontWeight: 700, textAlign: 'center', pb: 1 }}>
          {t("rate_ride") || "Rate Your Driver"}
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '8px 24px' }}>
          
          {/* Driver Profile Header */}
          {ratingBooking && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '8px', width: '100%' }}>
              <Avatar 
                src={ratingBooking.driver_image} 
                style={{ width: '80px', height: '80px', border: '3px solid #E9AE31', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              >
                <AccountCircleIcon style={{ width: '80px', height: '80px' }} />
              </Avatar>
              <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--text-main)', marginTop: '4px' }}>
                {ratingBooking.driver_name}
              </Typography>
              
              {/* Driver's Current Rating (Static Stars) */}
              {ratingBooking.driverRating ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <StarIcon style={{ color: '#E9AE31', fontSize: '18px' }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                    {ratingBooking.driverRating}
                  </Typography>
                </div>
              ) : null}

              {/* Vehicle Pill Badges */}
              {(ratingBooking.vehicleModel || ratingBooking.vehicleMake || ratingBooking.vehicle_number) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '2px' }}>
                  {ratingBooking.vehicleMake && (
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ratingBooking.vehicleMake}
                    </span>
                  )}
                  {ratingBooking.vehicleModel && (
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ratingBooking.vehicleModel}
                    </span>
                  )}
                  {ratingBooking.vehicle_number && (
                    <span style={{ backgroundColor: 'rgba(233, 174, 49, 0.12)', border: '1px solid rgba(233, 174, 49, 0.25)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: '#E9AE31', fontWeight: 600 }}>
                      {ratingBooking.vehicle_number}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ borderBottom: '1px solid var(--border-color)', width: '100%', margin: '4px 0' }} />

          <Typography variant="subtitle1" style={{ fontWeight: 700, color: 'var(--text-main)', textAlign: 'center' }}>
            {t("how_your_trip") || "How is your trip?"}
          </Typography>
          
          <Typography variant="body2" style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: '-10px' }}>
            {t("your_feedback_test") || "Your feedback will help us improve driving experience better."}
          </Typography>
          
          {/* Star Selection */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <IconButton 
                key={star} 
                onClick={() => setRatingValue(star)}
                style={{ padding: '8px' }}
              >
                {star <= ratingValue ? (
                  <StarIcon style={{ color: '#E9AE31', fontSize: '40px' }} />
                ) : (
                  <StarBorderIcon style={{ color: 'var(--text-secondary)', fontSize: '40px' }} />
                )}
              </IconButton>
            ))}
          </div>

          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder={t("your_feedback") || "Your feedback (optional) ..."}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            InputProps={{
              style: {
                color: 'var(--text-main)',
                backgroundColor: 'var(--bg-main)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px'
              }
            }}
          />
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 24px 24px' }}>
          <Button 
            onClick={handleSkipRating}
            style={{ color: 'var(--text-secondary)', textTransform: 'none', fontWeight: 600 }}
          >
            {t("skip") || "Skip"}
          </Button>
          <Button 
            variant="contained"
            onClick={handleSubmitRating}
            disabled={ratingValue === 0}
            style={{ 
              backgroundColor: ratingValue === 0 ? 'rgba(233, 174, 49, 0.4)' : '#E9AE31', 
              color: 'var(--bg-main)', 
              textTransform: 'none', 
              fontWeight: 700,
              borderRadius: '8px'
            }}
          >
            {t("submit") || "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
